import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

import { useScrollFade } from "~/hooks/useScrollFade";
import { cn } from "~/lib/utils";

/** Overflow hints stay outside the scroller so they never change its measured height. */
export function DockScrollContents({
  label,
  children,
  contentClassName,
  inset = true,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly contentClassName?: string;
  readonly inset?: boolean;
}) {
  const { ref, edges, onScroll } = useScrollFade<HTMLDivElement>("y");
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(onScroll);
    observer.observe(content);
    return () => observer.disconnect();
  }, [onScroll]);

  const mask =
    edges.start || edges.end
      ? `linear-gradient(to bottom, ${edges.start ? "transparent, black 20px" : "black"}, ${edges.end ? "black calc(100% - 20px), transparent" : "black"})`
      : undefined;
  const scroll = (direction: number) => {
    const viewport = ref.current;
    if (!viewport) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    viewport.scrollBy({
      top: direction * Math.max(28, viewport.clientHeight * 0.75),
      behavior: reduce ? "instant" : "smooth",
    });
  };

  return (
    <>
      <div
        className={cn(
          "ncl-rail-scroll h-full min-h-0 w-full overflow-y-auto overscroll-contain",
          inset && "pl-3",
        )}
        aria-label={`${label} scroll area`}
        tabIndex={0}
        data-dock-scroll-viewport={label}
        ref={ref}
        onScroll={onScroll}
        style={{ maskImage: mask, WebkitMaskImage: mask }}
      >
        <div
          className={cn("flex w-full flex-none flex-col", contentClassName)}
          data-dock-scroll-content=""
          ref={contentRef}
        >
          {children}
        </div>
      </div>
      {edges.start ? (
        <button
          type="button"
          aria-label={`Scroll ${label} up`}
          title={`Scroll ${label} up`}
          data-dock-scroll-edge="top"
          style={{ left: inset ? "calc(50% + 6px)" : "50%" }}
          className="absolute top-0 left-[calc(50%+6px)] flex size-6 -translate-x-1/2 items-center justify-center rounded-full surface-glass border border-foreground/20 text-foreground/90 [--glass-opacity:28%] [--glass-blur:4px] shadow-sm inset-shadow-[0_1px_0_color-mix(in_oklab,var(--foreground)_15%,transparent)] hover:border-foreground/40 hover:text-foreground hover:[--glass-opacity:45%] forced-colors:bg-[Canvas] forced-colors:text-[ButtonText] forced-colors:backdrop-filter-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => scroll(-1)}
        >
          <ChevronUpIcon className="size-3.5" />
        </button>
      ) : null}
      {edges.end ? (
        <button
          type="button"
          aria-label={`Scroll ${label} down`}
          title={`Scroll ${label} down`}
          data-dock-scroll-edge="bottom"
          style={{ left: inset ? "calc(50% + 6px)" : "50%" }}
          className="absolute bottom-0 left-[calc(50%+6px)] flex size-6 -translate-x-1/2 items-center justify-center rounded-full surface-glass border border-foreground/20 text-foreground/90 [--glass-opacity:28%] [--glass-blur:4px] shadow-sm inset-shadow-[0_1px_0_color-mix(in_oklab,var(--foreground)_15%,transparent)] hover:border-foreground/40 hover:text-foreground hover:[--glass-opacity:45%] forced-colors:bg-[Canvas] forced-colors:text-[ButtonText] forced-colors:backdrop-filter-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => scroll(1)}
        >
          <ChevronDownIcon className="size-3.5" />
        </button>
      ) : null}
    </>
  );
}
